import React, { useEffect, useRef } from "react";
import { View, StyleSheet } from "react-native";
import { useCheckoutContext } from "../../../context/CheckoutContext";
import { useUser } from "../../../context/UserContext";
import { BACKEND_URL } from "../../../Config/Const";

export default function PagarTarjetaCreditoDebito({ total, hash, urlConsulta, fechor, storename, oid, marcaTarjeta: marcaTarjetaProp, onReady, onSuccess, onCancel }) {
  const iframeRef = useRef(null);

  const { tipoPago, cuotas, marcaTarjeta: marcaTarjetaCtx } = useCheckoutContext();
  const { nombreUsuario,} = useUser();

  const safeAttr = (value) => JSON.stringify(value ?? '');
  const paymentMethodValue = marcaTarjetaProp || marcaTarjetaCtx || '';

  useEffect(() => {
    if (iframeRef.current) {
      const iframeDoc = iframeRef.current.contentWindow.document;
      const form = iframeDoc.getElementById("paymentForm");
      if (form) {
        form.submit();
        if (typeof onReady === 'function') onReady();
      }
    }
    // Fallback por si el submit demora
    const t = setTimeout(() => {
      if (typeof onReady === 'function') onReady();
    }, 2000);
    return () => clearTimeout(t);
  }, [total, hash]); // reenvía el formulario si cambian total o hash

  // Detectar cuando el iframe vuelve a nuestras URLs de respuesta (éxito o cancelación)
  useEffect(() => {
    const onIframeLoad = () => {
      if (!iframeRef.current) return;
      try {
        const href = iframeRef.current.contentWindow.location.href || '';
        // Consideramos éxito y fallo según endpoints configurados en el form
        if (href.includes('/web/pagoOK2.r')) {
          if (typeof onSuccess === 'function') onSuccess();
        } else if (href.includes('/web/pagoRE2.r')) {
          if (typeof onCancel === 'function') onCancel();
        }
      } catch (e) {
        // Mientras esté en dominio de la procesadora, no tenemos acceso (cross-origin). Ignorar.
      }
    };
    const iframe = iframeRef.current;
    if (iframe) iframe.addEventListener('load', onIframeLoad);
    return () => {
      if (iframe) iframe.removeEventListener('load', onIframeLoad);
    };
  }, [onSuccess, onCancel]);

  const htmlContent = `
    <html>
      <body>
        <form id="paymentForm" action=${safeAttr(urlConsulta)} method="POST">
          <input type="hidden" name="checkoutoption" value="combinedpage" />
          <input type="hidden" name="timezone" value="America/Buenos_Aires" />
          <input type="hidden" name="txndatetime" value=${safeAttr(fechor)} />
          <input type="hidden" name="hash_algorithm" value="SHA256" />
          <input type="hidden" name="hash" value=${safeAttr(hash)} />
          <input type="hidden" name="storename" value=${safeAttr(storename)} />
          <input type="hidden" name="mode" value="payonly" />
          <input type="hidden" name="chargetotal" value=${safeAttr((Number(total) || 0).toFixed(2))} />
          <input type="hidden" name="numberOfInstallments" value=${safeAttr(String(cuotas ?? ''))} />
          <input type="hidden" name="oid" value=${safeAttr(oid)} />
          <input type="hidden" name="txntype" value="sale" />
          <input type="hidden" name="paymentMethod" value=${safeAttr(paymentMethodValue)} />
          <input type="hidden" name="currency" value="032" />
          <input type="hidden" name="dynamicMerchantName" value="www.pinguino.com.ar" />
          <input type="hidden" name="cardFunction" value=${safeAttr(tipoPago === '1' ? 'credit' : 'debit')} />
          <input type="hidden" name="language" value="es_ES" />
          ${paymentMethodValue === 'M' ? '<input type="hidden" name="authenticateTransaction" value="true"/>' : ''}
          ${paymentMethodValue === 'M' ? '<input type="hidden" name="threeDSRequestorChallengeIndicator" value="1"/>' : ''}
          <input type="hidden" name="mobileMode" value="true" />
          <input type="hidden" name="parentUri" value="${document.location.protocol + '//' + document.location.host}" />
          <input type="hidden" name="comments" value=${safeAttr(String(nombreUsuario || ''))} />
          <input type="hidden" name="responseFailURL" value=${safeAttr(`${BACKEND_URL}/web/pagoRE2.r`)} />
          <input type="hidden" name="responseSuccessURL" value=${safeAttr(`${BACKEND_URL}/web/pagoOK2.r`)} />
          <input type="hidden" name="transactionNotificationURL" value=${safeAttr(`${BACKEND_URL}/rest/fin/webhookFiserv`)} />
          <input type="hidden" name="hexColorCode" value="#e62123" />
        </form>
        <script>
          document.getElementById("paymentForm").submit();
        </script>
      </body>
    </html>
  `;

  return (
    <View style={styles.container}>
      {/* Fondo modal oscuro con patrón grid */}
      <View style={styles.modalOverlay} />
      
      {/* Contenedor del iframe con sombra */}
      <View style={styles.iframeWrapper}>
        <iframe
          ref={iframeRef}
          title="payment-frame"
          style={styles.iframe}
          srcDoc={htmlContent}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { 
    flex: 1, 
    justifyContent: "center", 
    alignItems: "center",
    position: "relative",
  },
  modalOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    backgroundImage: `
      linear-gradient(0deg, transparent 24%, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.05) 26%, transparent 27%, transparent 74%, rgba(255, 255, 255, 0.05) 75%, rgba(255, 255, 255, 0.05) 76%, transparent 77%, transparent),
      linear-gradient(90deg, transparent 24%, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.05) 26%, transparent 27%, transparent 74%, rgba(255, 255, 255, 0.05) 75%, rgba(255, 255, 255, 0.05) 76%, transparent 77%, transparent)
    `,
    backgroundSize: "50px 50px",
    zIndex: 1,
  },
  iframeWrapper: {
    width: "90%",
    height: "90%",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
    zIndex: 2,
    position: "relative",
  },
  iframe: { 
    width: "100%", 
    height: "100%", 
    border: "none",
    borderRadius: 8,
  },
});

